import React, { useEffect, useState } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Button,
    Grid,
    Paper,
    Typography,
    Divider,
    CircularProgress,
    Stack,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import {
    makeGETRequestOnUserService,
    makePOSTRequestOnUserService,
    makePUTRequestOnUserService,
    makeDELETERequestOnUserServiceWithQueryParameter,
} from "../../Api/api";

const BRAND = "#262349";
const BRAND_HOVER = "#1d1a3a";

export const BusinessProfile = ({
    currentUser,
    getErrorMessage,
}: any) => {
    const toastr = require("toastr");

    const [loading, setLoading] = useState(false);
    const [docLoading, setDocLoading] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteDocsDialogOpen, setDeleteDocsDialogOpen] = useState(false);
    const [documentUpdate, setDocumentUpdate] = useState(false);

    const [formData, setFormData] = useState({
        business_name: "",
        email_id: "",
        contact_number: "",
        contact_person_name: "",
        address: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        pin_code: "",
        is_country_party_registered: false,
        ucc: "",
    });

    const [documents, setDocuments] = useState({
        pan_card: null as File | null,
        gst_info: null as File | null,
        arn_card: null as File | null,
    });

    const [documentUrls, setDocumentUrls] = useState({
        pan_card_file: "",
        gst_info_file: "",
        arn_card_file: "",
    });

    const validateForm = () => {
        const requiredFields = [
            "business_name",
            "email_id",
            "contact_number",
            "contact_person_name",
            "address",
            "city",
            "state",
            "pin_code",
            "ucc",
        ];

        for (const field of requiredFields) {
            if (!formData[field as keyof typeof formData]?.toString().trim()) {
                toastr.error(
                    `${field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())} is required`
                );
                return false;
            }
        }

        return true;
    };

    const fetchBusinessProfile = async () => {
        try {
            const response = await makeGETRequestOnUserService(
                `/partneruser/retrieve_business_profile/${currentUser.uuid}/`
            );

            if (response.status === 200) {
                setIsUpdate(true);

                setFormData({
                    business_name: response.data.business_name || "",
                    email_id: response.data.email_id || "",
                    contact_number: response.data.contact_number || "",
                    contact_person_name:
                        response.data.contact_person_name || "",
                    address: response.data.address || "",
                    address1: response.data.address1 || "",
                    address2: response.data.address2 || "",
                    city: response.data.city || "",
                    state: response.data.state || "",
                    pin_code: response.data.pin_code || "",
                    is_country_party_registered:
                        response.data.is_country_party_registered || false,
                    ucc: response.data.ucc || "",
                });
            }
        } catch (error) {
            setIsUpdate(false);
        }
    };

    const fetchDocuments = async () => {
        try {
            const response = await makeGETRequestOnUserService(
                `/partneruser/retrieve_document/${currentUser.uuid}/`
            );

            const documentData = response.data.document_data;

            setDocumentUrls({
                pan_card_file: documentData.pan_card_file || "",
                gst_info_file: documentData.gst_info_file || "",
                arn_card_file: documentData.arn_card_file || "",
            });

            setDocumentUpdate(true);
        } catch (error) {
            setDocumentUpdate(false);
        }
    };

    useEffect(() => {
        if (currentUser?.uuid) {
            fetchBusinessProfile();
            fetchDocuments();
        }
    }, [currentUser?.uuid]);

    const handleChange = (e: any) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFileChange = (e: any) => {
        const { name, files } = e.target;

        if (!files || !files[0]) return;

        setDocuments((prev) => ({
            ...prev,
            [name]: files[0],
        }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setLoading(true);

            const payload = {
                user_id: currentUser.uuid,
                ...formData,
            };

            if (isUpdate) {
                await makePUTRequestOnUserService(
                    `/partneruser/update_business_profile/${currentUser.uuid}/`,
                    payload
                );

                toastr.success("Business Profile updated successfully");
            } else {
                await makePOSTRequestOnUserService(
                    "/partneruser/create_business_profile/",
                    payload
                );

                toastr.success("Business Profile created successfully");

                setIsUpdate(true);
            }
        } catch (error: any) {
            toastr.error(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentSubmit = async () => {
        try {
            setDocLoading(true);

            const formDataObj = new FormData();

            formDataObj.append("user_id", currentUser.uuid);

            if (documents.pan_card) {
                formDataObj.append("pan_card", documents.pan_card);
            }

            if (documents.gst_info) {
                formDataObj.append("gst_info", documents.gst_info);
            }

            if (documents.arn_card) {
                formDataObj.append("arn_card", documents.arn_card);
            }

            if (documentUpdate) {
                await makePUTRequestOnUserService(
                    `/partneruser/update_document/${currentUser.uuid}/`,
                    formDataObj
                );

                toastr.success("Documents updated successfully");
            } else {
                if (!documents.pan_card || !documents.gst_info) {
                    toastr.error("PAN Card and GST Info are required");
                    setDocLoading(false);
                    return;
                }

                await makePOSTRequestOnUserService(
                    "/partneruser/upload_document/",
                    formDataObj
                );

                toastr.success("Documents uploaded successfully");
                setDocumentUpdate(true);
            }

            setDocuments({
                pan_card: null,
                gst_info: null,
                arn_card: null,
            });

            fetchDocuments();
        } catch (error: any) {
            toastr.error(getErrorMessage(error));
        } finally {
            setDocLoading(false);
        }
    };

    const getFileType = (base64: string) => {
        if (!base64) return "";
        if (base64.startsWith("JVBER")) {
            return "pdf";
        }
        if (base64.startsWith("iVBOR")) {
            return "png";
        }
        if (base64.startsWith("/9j/")) {
            return "jpeg";
        }
        return "unknown";
    };

    const getPreviewUrlFromBase64 = (base64: string) => {
        if (!base64) return "";

        const fileType = getFileType(base64);

        switch (fileType) {
            case "pdf":
                return `data:application/pdf;base64,${base64}`;

            case "png":
                return `data:image/png;base64,${base64}`;

            case "jpeg":
                return `data:image/jpeg;base64,${base64}`;

            default:
                return "";
        }
    };

    const renderPreview = (value: string) => {
        const previewUrl = getPreviewUrlFromBase64(value);

        if (!previewUrl) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        height: 160,
                        border: "1.5px dashed #d6d6e3",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        background: "#fafafc",
                        color: "#9a99ad",
                    }}
                >
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 28, color: "#9a99ad" }} />
                    <Typography variant="caption" sx={{ color: "#9a99ad" }}>
                        No file uploaded yet
                    </Typography>
                </Box>
            );
        }

        const isPdf = previewUrl.startsWith("data:application/pdf");

        return (
            <Box
                sx={{
                    width: "100%",
                    height: 160,
                    border: "1px solid #e6e6ef",
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "#fafafc",
                }}
            >
                {isPdf ? (
                    <iframe
                        src={previewUrl}
                        title="PDF Preview"
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                        }}
                    />
                ) : (
                    <img
                        src={previewUrl}
                        alt="Document preview"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                )}
            </Box>
        );
    };

    const renderSelectedFile = (file: File | null) => {
        if (!file) return null;

        const url = URL.createObjectURL(file);

        return (
            <Box
                sx={{
                    width: "100%",
                    height: 160,
                    border: `1.5px solid ${BRAND}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#fafafc",
                }}
            >
                {file.type === "application/pdf" ? (
                    <iframe
                        src={url}
                        title="PDF Preview"
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                        }}
                    />
                ) : (
                    <img
                        src={url}
                        alt="Selected file preview"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                )}
            </Box>
        );
    };

    const handleDelete = async () => {
        try {
            setLoading(true);

            await makeDELETERequestOnUserServiceWithQueryParameter(
                `/partneruser/delete_business_profile/${currentUser.uuid}/`
            );

            toastr.success("Business Profile deleted successfully");

            setFormData({
                business_name: "",
                email_id: "",
                contact_number: "",
                contact_person_name: "",
                address: "",
                address1: "",
                address2: "",
                city: "",
                state: "",
                pin_code: "",
                is_country_party_registered: false,
                ucc: "",
            });

            setIsUpdate(false);
            setDeleteDialogOpen(false);
        } catch (error: any) {
            toastr.error(getErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    const handleDocumentDelete = async () => {
        try {
            setDocLoading(true);

            await makeDELETERequestOnUserServiceWithQueryParameter(
                `/partneruser/delete_document/${currentUser.uuid}/`
            );

            toastr.success("Documents deleted successfully");

            setDocuments({
                pan_card: null,
                gst_info: null,
                arn_card: null,
            });

            setDocumentUrls({
                pan_card_file: "",
                gst_info_file: "",
                arn_card_file: "",
            });

            setDocumentUpdate(false);
            setDeleteDocsDialogOpen(false);
        } catch (error: any) {
            toastr.error(getErrorMessage(error));
        } finally {
            setDocLoading(false);
        }
    };

    const documentRows = [
        {
            key: "pan_card" as const,
            label: "PAN Card",
            required: true,
            file: documents.pan_card,
            url: documentUrls.pan_card_file,
        },
        {
            key: "gst_info" as const,
            label: "GST Info",
            required: true,
            file: documents.gst_info,
            url: documentUrls.gst_info_file,
        },
        {
            key: "arn_card" as const,
            label: "ARN Card",
            required: false,
            file: documents.arn_card,
            url: documentUrls.arn_card_file,
        },
    ];

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            height: "100%",
                            borderRadius: 3,
                            border: "1px solid #ececf3",
                        }}
                    >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Profile
                            </Typography>
                        </Stack>

                        <Divider sx={{ mb: 1 }} />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="Business Name"
                                    name="business_name"
                                    value={formData.business_name}
                                    onChange={handleChange}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="UCC"
                                    name="ucc"
                                    value={formData.ucc}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="Contact Person Name"
                                    name="contact_person_name"
                                    value={formData.contact_person_name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="Contact Number"
                                    name="contact_number"
                                    value={formData.contact_number}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            required
                            fullWidth
                            margin="normal"
                            label="Email"
                            name="email_id"
                            value={formData.email_id}
                            onChange={handleChange}
                        />

                        <TextField
                            required
                            fullWidth
                            margin="normal"
                            label="Address"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                        />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Address Line 2"
                                    name="address1"
                                    value={formData.address1}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Address Line 3"
                                    name="address2"
                                    value={formData.address2}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="City"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="State"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    required
                                    fullWidth
                                    margin="normal"
                                    label="Pin Code"
                                    name="pin_code"
                                    value={formData.pin_code}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>

                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={loading}
                                startIcon={
                                    loading ? (
                                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    ) : undefined
                                }
                                sx={{
                                    backgroundColor: BRAND,
                                    textTransform: "none",
                                    px: 3,
                                    "&:hover": { backgroundColor: BRAND_HOVER },
                                }}
                            >
                                {loading
                                    ? "Saving..."
                                    : isUpdate
                                        ? "Save changes"
                                        : "Create profile"}
                            </Button>

                            {isUpdate && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    disabled={loading}
                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                    sx={{ textTransform: "none" }}
                                >
                                    Delete profile
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Grid>


                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 2.5, md: 3.5 },
                            height: "100%",
                            borderRadius: 3,
                            border: "1px solid #ececf3",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 0.5 }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    Documents
                                </Typography>
                            </Stack>

                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Stack spacing={3}>
                            {documentRows.map((row) => {
                                const hasValue = !!(row.file || row.url);

                                return (
                                    <Box key={row.key}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5 }}
                                            >
                                                {row.label}
                                                {row.required && (
                                                    <Box component="span" sx={{ color: "#d32f2f" }}>
                                                        *
                                                    </Box>
                                                )}
                                            </Typography>


                                        </Stack>

                                        <Grid container spacing={2} alignItems="flex-start">
                                            <Grid item xs={12} sm={6}>
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    fullWidth
                                                    startIcon={<CloudUploadOutlinedIcon sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        height: 160,
                                                        textTransform: "none",
                                                        borderRadius: 2,
                                                        borderStyle: "dashed",
                                                        borderColor: row.file ? BRAND : "#d6d6e3",
                                                        color: row.file ? BRAND : "#5b5a70",
                                                        flexDirection: "column",
                                                        gap: 1,
                                                        "&:hover": {
                                                            borderColor: BRAND,
                                                            backgroundColor: "#faf9fd",
                                                        },
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {row.file
                                                            ? "Change file"
                                                            : hasValue
                                                                ? "Replace file"
                                                                : "Choose file"}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: "#9a99ad",
                                                            textAlign: "center",
                                                            px: 2,
                                                            wordBreak: "break-all",
                                                        }}
                                                    >
                                                        {row.file
                                                            ? row.file.name
                                                            : "PDF, JPG or PNG"}
                                                    </Typography>
                                                    <input
                                                        type="file"
                                                        name={row.key}
                                                        hidden
                                                        onChange={handleFileChange}
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                    />
                                                </Button>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                {row.file
                                                    ? renderSelectedFile(row.file)
                                                    : renderPreview(row.url)}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={handleDocumentSubmit}
                                disabled={docLoading}
                                startIcon={
                                    docLoading ? (
                                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                                    ) : undefined
                                }
                                sx={{
                                    backgroundColor: BRAND,
                                    textTransform: "none",
                                    px: 3,
                                    "&:hover": { backgroundColor: BRAND_HOVER },
                                }}
                            >
                                {docLoading
                                    ? "Saving..."
                                    : documentUpdate
                                        ? "Update documents"
                                        : "Upload documents"}
                            </Button>

                            {documentUpdate && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setDeleteDocsDialogOpen(true)}
                                    disabled={docLoading}
                                    startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                    sx={{ textTransform: "none" }}
                                >
                                    Delete documents
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Delete business profile</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        This will permanently remove your business profile details.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={loading}
                        sx={{ textTransform: "none" }}
                    >
                        {loading ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDocsDialogOpen}
                onClose={() => setDeleteDocsDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Delete documents</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        This will permanently remove all uploaded KYC documents for this
                        account.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDocsDialogOpen(false)} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDocumentDelete}
                        disabled={docLoading}
                        sx={{ textTransform: "none" }}
                    >
                        {docLoading ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};